export const STATUS_HEADER_VALUE_PREFIX = '__status__:';

export function isStatusHeaderOption(option) {
	return Boolean(option?.isStatusHeader);
}

export function groupsFromStatusOptions(statusOptions = []) {
	return statusOptions.map(status => ({
		value: status.value,
		label: status.label
	}));
}

export function getSelectionState(stageValues = [], selectedValues = []) {
	if (!stageValues.length) return 'unchecked';
	const selected = new Set(selectedValues);
	const count = stageValues.filter(value => selected.has(value)).length;
	if (count === 0) return 'unchecked';
	if (count === stageValues.length) return 'checked';
	return 'indeterminate';
}

export function getSelectionStateLabel(state) {
	if (state === 'checked') return 'all selected';
	if (state === 'indeterminate') return 'partially selected';
	return 'none selected';
}

export function ariaCheckedForState(state) {
	if (state === 'indeterminate') return 'mixed';
	if (state === 'checked') return 'true';
	return 'false';
}

/**
 * Unchecked or indeterminate selects every child. Checked clears them.
 * Already-selected stages keep their relative order; newly added stages
 * append in canonical group order.
 */
export function toggleStatusStages(
	stageValues = [],
	selectedOptions = [],
	optionsByValue = new Map()
) {
	const selected = (selectedOptions || []).filter(
		option => option && !isStatusHeaderOption(option)
	);
	const state = getSelectionState(
		stageValues,
		selected.map(option => option.value)
	);

	if (state === 'checked') {
		const drop = new Set(stageValues);
		return selected.filter(option => !drop.has(option.value));
	}

	const have = new Set(selected.map(option => option.value));
	const next = selected.slice();
	stageValues.forEach(value => {
		if (have.has(value)) return;
		const option = optionsByValue.get(value);
		if (option) next.push(option);
	});
	return next;
}

export function filterStageMenuOption(candidate, rawInput) {
	const query = String(rawInput || '')
		.trim()
		.toLowerCase();
	if (!query) return true;

	if (candidate?.data?.isStatusHeader) {
		return (candidate.data.stageLabels || []).some(label =>
			String(label).toLowerCase().includes(query)
		);
	}

	return String(candidate?.label || '')
		.toLowerCase()
		.includes(query);
}

/**
 * Groups flat stage options under status headers.
 * `groups[].stages` fixes the order for that status. Stages not listed there
 * are placed by `stageToStatus`, then `statusGroupMap` when the stored status
 * and the filter group differ.
 */
export function buildStageFilterOptions(
	stageOptions = [],
	{ groups = [], stageToStatus = {}, statusGroupMap = {} } = {}
) {
	const byValue = new Map(
		(stageOptions || [])
			.filter(option => option?.value)
			.map(option => [option.value, option])
	);
	const used = new Set();
	const buckets = (groups || []).map(group => ({
		value: group.value,
		label: group.label,
		stages: (group.stages || [])
			.map(value => byValue.get(value))
			.filter(Boolean)
	}));

	buckets.forEach(group => {
		group.stages.forEach(stage => used.add(stage.value));
	});

	const bucketByValue = new Map(buckets.map(group => [group.value, group]));

	(stageOptions || []).forEach(option => {
		if (!option?.value || used.has(option.value)) return;
		const rawStatus = stageToStatus[option.value];
		const filterStatus = statusGroupMap[rawStatus] || rawStatus;
		const group = bucketByValue.get(filterStatus) || buckets[0];
		if (!group) return;
		group.stages.push(option);
		used.add(option.value);
	});

	return buckets
		.filter(group => group.stages.length > 0)
		.map(group => {
			const stageValues = group.stages.map(stage => stage.value);
			const stageLabels = group.stages.map(stage => stage.label);
			return {
				label: group.label,
				options: [
					{
						label: group.label,
						value: `${STATUS_HEADER_VALUE_PREFIX}${group.value}`,
						isStatusHeader: true,
						statusValue: group.value,
						stageValues,
						stageLabels
					},
					...group.stages.map(stage => ({
						...stage,
						statusValue: group.value
					}))
				]
			};
		});
}
